/**
 * AppModeZeroState Component
 *
 * Shows entry points so users immediately understand all the ways they can
 * get items into a tool: by typing/pasting, uploading a CSV, or configuring
 * a workspace session.
 */

import { Database, PencilLine, FileUp } from 'lucide-react';

export interface AppModeZeroStateProps {
  /** Tool name for display */
  toolName: string;

  /** Number of shaped items available in workspace */
  shapedItemCount: number;

  /**
   * Opens the modal pre-set to the workspace/session config picker. Leave it
   * out when the person may not configure a session: the path is then not
   * drawn, and `importRefusedLine` is shown in its place (build contract §2.8).
   */
  onImportClick?: () => void;

  /** The line drawn instead of Configure Session when `onImportClick` is absent. */
  importRefusedLine?: string;

  /** Opens the modal pre-set to manual text entry */
  onManualClick?: () => void;

  /** Opens the modal pre-set to CSV upload */
  onUploadClick?: () => void;

  /** Optional custom description */
  description?: string;

  /** Additional className */
  className?: string;
}

export function AppModeZeroState({
  toolName,
  shapedItemCount,
  onImportClick,
  importRefusedLine,
  onManualClick,
  onUploadClick,
  description,
  className = '',
}: AppModeZeroStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center py-10 px-4 sm:py-16 sm:px-8 ${className}`}>
      <div className="w-full max-w-2xl text-center">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
          Add items to get started
        </h2>
        <p className="text-[var(--text-ter)] mb-10 text-sm">
          {description ?? `Choose how you'd like to bring items into ${toolName}.`}
        </p>

        <div className="space-y-4">
          {/* Configure Session — primary path */}
          {onImportClick ? (
            <button
              onClick={onImportClick}
              className="group w-full flex items-center gap-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-4 sm:px-6 py-5 text-left hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/60 transition-colors shrink-0">
                <Database className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--text-pri)] text-sm">Configure Session</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  {shapedItemCount > 0
                    ? `Set up a workspace session — ${shapedItemCount} item${shapedItemCount !== 1 ? 's' : ''} available`
                    : 'Set up a workspace session (no items yet)'}
                </p>
              </div>
              {shapedItemCount > 0 && (
                <span className="hidden sm:inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900/40 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-400 shrink-0">
                  {shapedItemCount} available
                </span>
              )}
            </button>
          ) : importRefusedLine ? (
            <p className="text-sm text-[var(--text-ter)]" data-testid="zero-state-configure-refused">
              {importRefusedLine}
            </p>
          ) : null}

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[var(--hover-tint)]" />
            <span className="text-xs text-[var(--text-ter)] font-medium">or add items directly</span>
            <div className="flex-1 h-px bg-[var(--hover-tint)]" />
          </div>

          {/* Quick-add paths */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Type or Paste */}
            <button
              onClick={onManualClick ?? onImportClick}
              className="group flex flex-col items-center gap-3 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-4 sm:px-6 py-8 text-center hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 group-hover:bg-[var(--slate-dim)] dark:group-hover:bg-[var(--slate)]/60 transition-colors">
                <PencilLine className="h-6 w-6 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-pri)] text-sm">Type or Paste</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">Enter items directly, one per line</p>
              </div>
            </button>

            {/* Upload CSV */}
            <button
              onClick={onUploadClick ?? onManualClick ?? onImportClick}
              className="group flex flex-col items-center gap-3 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-4 sm:px-6 py-8 text-center hover:border-[var(--green)] dark:hover:border-[var(--green)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/20 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--green-dim)] dark:bg-[var(--green)]/40 group-hover:bg-[var(--green-dim)] dark:group-hover:bg-[var(--green)]/60 transition-colors">
                <FileUp className="h-6 w-6 text-[var(--green)] dark:text-[var(--text-ter)]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-pri)] text-sm">Upload CSV</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">Import from a spreadsheet or template</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppModeZeroState;
